import { ChevronDown, Plus, LogOut, Users, Palette } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '@/components/ui/DropdownMenu';
import { useSitzungsWechsel } from '@/hooks/useSitzungsWechsel';
import {
  NutzerHinzufuegen,
  NUTZER_HINZUFUEGEN
} from '@/components/auth/NutzerHinzufuegen';
import { sessionLabel, userLabel } from '@/lib/format';
import { NutzerChip } from '@/components/ui/NutzerChip';
import { SymbolWahl, SYMBOL_WAEHLEN } from '@/components/auth/SymbolWahl';
import { useState } from 'react';
import { FOKUS_RING } from '@/lib/fokus';

// A9.1: Statt des reinen Farbpunkts steht hier der Identitäts-Chip.
// Der Punkt sagte, DASS jemand angemeldet ist; der Chip sagt, WER — und
// zwar mit einem Zeichen, das man aussprechen kann. Genau darum geht es
// beim Nutzerwechsel: „ich bin nicht die Tasse" ist ein Satz, den jemand
// am Tresen sagt.

export function SessionSwitcher({
  kompakt = false
}: {
  /**
   * Obere Leiste am PC: nur der Farbpunkt mit Kürzel (Skizze vom
   * 03.11.2026, Punkt 5) — der Name steht im Menü und wird vorgelesen.
   */
  kompakt?: boolean;
} = {}) {
  const { me, andere, wechseln, abmelden, hinzufuegen, nutzerHinzufuegen } =
    useSitzungsWechsel();
  const [symbolWahl, setSymbolWahl] = useState(false);

  if (me === null) {
    return null;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-tour="benutzermenue"
            className={`inline-flex min-h-[44px] items-center gap-2 rounded-md px-3 py-2 text-sm text-ink hover:bg-cream-dark ${FOKUS_RING}`}
          >
            <NutzerChip
              farbe={me.farbe}
              symbol={me.symbol}
              kuerzel={me.kuerzel}
              name={userLabel(me)}
            />
            <span className={kompakt ? 'sr-only' : 'font-medium'}>
              {userLabel(me)}
            </span>
            <ChevronDown className="h-4 w-4 text-ink-faint" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent>
          {/* Wortlaut wie im Mobil-Menü: dort heisst dieselbe Zeile seit je
            „Angemeldet". Zwei Wörter für denselben Zustand sind genau die
            Sorte Abweichung, aus der P4 entstanden ist. */}
          <DropdownMenuLabel>Angemeldet</DropdownMenuLabel>
          <DropdownMenuItem disabled>
            {/* Groß, weil hier die Identität selbst das Thema ist. */}
            <NutzerChip
              farbe={me.farbe}
              symbol={me.symbol}
              kuerzel={me.kuerzel}
              name={userLabel(me)}
              groesse="gross"
            />
            <div className="flex flex-col">
              <span>{userLabel(me)}</span>
              <span className="text-xs text-ink-faint">{me.nc_username}</span>
            </div>
          </DropdownMenuItem>

          {andere.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Wechseln zu</DropdownMenuLabel>
              {andere.map((s) => (
                <DropdownMenuItem
                  key={s.username}
                  onSelect={() => {
                    void wechseln(s.username);
                  }}
                >
                  <NutzerChip
                    farbe={s.farbe}
                    symbol={s.symbol}
                    kuerzel={s.kuerzel}
                    name={sessionLabel(s)}
                    groesse="gross"
                  />
                  <span>{sessionLabel(s)}</span>
                </DropdownMenuItem>
              ))}
            </>
          )}

          <DropdownMenuSeparator />
          {/* Führt nicht mehr stur in den Anmeldeablauf. Am Tresen-iPad kam
            dort ohne Zwischenschritt immer derselbe Nextcloud-Nutzer an,
            und ein Abmelden war aus dem Ablauf heraus nicht möglich — der
            Wechsel war eine Sackgasse (P4). Denselben Weg geht seit K5
            schon das Mobil-Menü; er liegt jetzt im gemeinsamen Hook, damit
            die beiden nicht wieder auseinanderlaufen.

            Wortlaut wie mobil: „Weiteren Nutzer anmelden" sagt, was
            passiert — „hinzufügen" klang nach einem Verwaltungsschritt,
            und die bisherige Sitzung bleibt ja bestehen. */}
          {/* A9.1: Das Zeichen wechseln. Steht bewusst hier und nicht in
              den Einstellungen — es ist eine Sache der Identität, und die
              wird an dieser Stelle verhandelt. */}
          <DropdownMenuItem onSelect={() => setSymbolWahl(true)}>
            <Palette className="h-4 w-4 text-ink-soft" />
            {SYMBOL_WAEHLEN}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={hinzufuegen}>
            <Plus className="h-4 w-4 text-ink-soft" />
            {NUTZER_HINZUFUEGEN}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              void abmelden('current');
            }}
          >
            <LogOut className="h-4 w-4 text-ink-soft" />
            Abmelden
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              void abmelden('all');
            }}
          >
            <Users className="h-4 w-4 text-ink-soft" />
            Alle abmelden
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <NutzerHinzufuegen {...nutzerHinzufuegen} />

      {/* Erst mounten, wenn er offen ist. Der Dialog hält eine Mutation
          und einen Toast — ein geschlossener Dialog braucht beides nicht,
          und unbedingt gemountet zwänge er jeden Renderer dieses Menüs zu
          einem ToastProvider. Genau daran sind beim ersten Anlauf vier
          bestehende Tests gescheitert. */}
      {symbolWahl && (
        <SymbolWahl
          open
          onClose={() => setSymbolWahl(false)}
          farbe={me.farbe}
          symbol={me.symbol}
          kuerzel={me.kuerzel}
        />
      )}
    </>
  );
}
